import React, { useEffect, useState } from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Tooltip,
} from "@mui/material";
import {
  Verified as VerifiedIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchOrganizations,
  createOrganization,
} from "../../redux/actions/organizationActions";
import { RootState, AppDispatch } from "../../redux/store";
import { format } from "date-fns";
import Fuse from "fuse.js";

// Create Organization Dialog Component
const CreateOrganizationDialog: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const dispatch = useDispatch<AppDispatch>();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setName("");
  };

  const handleSubmit = () => {
    if (name.trim()) {
      dispatch(createOrganization({ name }))
        .unwrap()
        .then(() => {
          toast.success("Organization created successfully");
          handleClose();
        })
        .catch((error) => {
          toast.error(`Failed to create organization: ${error.message}`);
        });
    } else {
      toast.error("Organization name cannot be empty");
    }
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleClickOpen}>
        Create
      </Button>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Create New Organization</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter the name for the new organization.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Organization Name"
            type="text"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const OrganizationsTab: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { organizations, loading, error } = useSelector(
    (state: RootState) => state.organizations
  );
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    dispatch(fetchOrganizations());
  }, [dispatch]);

  const handleViewOrganization = (id: string) => {
    navigate(`/dashboard/organizations/${id}`);
  };

  // Filter organizations based on search query
  const filteredOrganizations = searchQuery
    ? new Fuse(organizations, {
        keys: ["name", "id"],
        threshold: 0.3,
      })
        .search(searchQuery)
        .map((result) => result.item)
    : organizations;

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Organization Name",
      flex: 1,
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      renderCell: (params: any) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
          }}
        >
          <span>{params.value}</span>
          {params.row.is_prod_release && (
            <Tooltip title="Production Release Organization">
              <VerifiedIcon sx={{ fontSize: 16, color: "#1976d2" }} />
            </Tooltip>
          )}
          {params.row.requires_redaction && (
            <Tooltip title="Requires Redaction">
              <VisibilityOffIcon sx={{ fontSize: 16, color: "#ed6c02" }} />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: "id",
      headerName: "Organization ID",
      flex: 2,
      minWidth: 200,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "created_at",
      headerName: "Created At",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      valueGetter: (params: any) => {
        return params ? format(new Date(params), "yyyy-MM-dd HH:mm:ss") : null;
      },
    },
    {
      field: "build_name",
      headerName: "Build Name",
      flex: 2,
      minWidth: 250,
      headerAlign: "center",
      align: "center",
      renderCell: (params: any) => {
        const buildName = params.row.active_deployment?.build?.build_name;
        const buildId = params.row.active_deployment?.build?.id;

        if (!buildName) {
          return (
            <span style={{ color: "#999" }}>
              No build or Testbed deployment
            </span>
          );
        }

        if (buildId) {
          return (
            <Link
              href={`/dashboard/builds/${buildId}`}
              underline="hover"
              sx={{ cursor: "pointer" }}
            >
              {buildName}
            </Link>
          );
        }

        return <span>{buildName}</span>;
      },
    },
    {
      field: "min_hosts",
      headerName: "Min Hosts",
      flex: 0.5,
      minWidth: 10,
      headerAlign: "center",
      align: "center",
      renderCell: (params: any) => {
        const minHosts = params.row.active_deployment?.min_hosts;
        if (minHosts === undefined || minHosts === null) {
          return (
            <span style={{ color: "#999" }}>
              No build or Testbed deployment
            </span>
          );
        }

        return <span>{minHosts}</span>;
      },
    },
    {
      field: "max_hosts",
      headerName: "Max Hosts",
      flex: 0.5,
      minWidth: 10,
      headerAlign: "center",
      align: "center",
      renderCell: (params: any) => {
        const maxHosts = params.row.active_deployment?.max_hosts;
        if (maxHosts === undefined) {
          return (
            <span style={{ color: "#999" }}>
              No build or Testbed deployment
            </span>
          );
        }
        if (maxHosts === null) {
          return <span>Unlimited</span>;
        }

        return <span>{maxHosts}</span>;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      minWidth: 150,
      renderHeader: () => <CreateOrganizationDialog />,
      renderCell: (params: any) => (
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleViewOrganization(params.row.id)}
        >
          View
        </Button>
      ),
      headerAlign: "center",
      align: "center",
    },
  ];

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <>
      <ToastContainer position="top-center" autoClose={3000} />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mt: 2,
          width: "100%",
        }}
      >
        <TextField
          label="Search Organizations"
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ flex: 1 }}
        />
        <Button
          variant="outlined"
          onClick={() => {
            setSearchQuery("");
          }}
        >
          Reset Filters
        </Button>
      </Box>

      <Box sx={{ height: "65vh", width: "100%", mt: 2 }}>
        <DataGrid
          rows={filteredOrganizations}
          columns={columns}
          getRowId={(row) => row.id}
        />
      </Box>
    </>
  );
};

export default OrganizationsTab;
